import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Database, Trophy, Calendar, Users, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const ImportConfirmation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, needs-selection, importing, success, error
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [maxFamilyMembers, setMaxFamilyMembers] = useState(1);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTokenData();
  }, [token]);

  const loadTokenData = async () => {
    try {
      const response = await axios.get(getApiUrl(`/api/data-import/confirm/${token}`));
      
      if (response.data.needsSelection) {
        setMembers(response.data.members);
        setMaxFamilyMembers(response.data.maxFamilyMembers);
        setStatus('needs-selection');
      } else {
        // Auto-import
        setStatus('importing');
        confirmImport();
      }
    } catch (error) {
      console.error('Error loading import data:', error);
      setError(error.response?.data?.error || 'Failed to load import data');
      setStatus('error');
    }
  };

  const confirmImport = async () => {
    const data = status === 'needs-selection' 
      ? { memberIds: selectedMembers }
      : {};

    setStatus('importing');
    
    axios.post(getApiUrl(`/api/data-import/confirm/${token}`), data)
      .then(response => {
        if (response.data.success) {
          setImportResults(response.data.data);
          setStatus('success');
        } else {
          setError(response.data.message || 'Import failed');
          setStatus('error');
        }
      })
      .catch(error => {
        console.error('Error confirming import:', error);
        setError(error.response?.data?.error || 'Import failed');
        setStatus('error');
      });
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else if (prev.length < maxFamilyMembers) {
        return [...prev, memberId];
      }
      return prev;
    });
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Import Failed</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={handleGoToDashboard}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'needs-selection') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Family Members to Import</h2>
            <p className="text-gray-600 mb-6">
              We found {members.length} {members.length === 1 ? 'member' : 'members'} associated with your email. 
              You can import up to {maxFamilyMembers === -1 ? 'unlimited' : maxFamilyMembers} family members.
            </p>

            <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
              {members.map((member, index) => (
                <label
                  key={member.memberId}
                  className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    selectedMembers.includes(member.memberId.toString())
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.memberId.toString())}
                        onChange={() => handleSelectMember(member.memberId.toString())}
                        className="w-5 h-5 text-blue-600"
                        disabled={!selectedMembers.includes(member.memberId.toString()) && selectedMembers.length >= maxFamilyMembers}
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {member.associationName}
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                Selected {selectedMembers.length} of {maxFamilyMembers === -1 ? members.length : maxFamilyMembers} members
              </p>
            </div>

            <button
              onClick={confirmImport}
              disabled={selectedMembers.length === 0}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Import Selected Members
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'importing') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Importing Your Data</h2>
          <p className="text-gray-600">Please wait while we import your entries, results, and memberships...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Complete!</h2>
              <p className="text-gray-600">
                Your data has been successfully imported into MyTrailer
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Family Members</div>
                  <div className="text-sm text-gray-600">
                    {importResults?.membersImported?.length || 0} imported
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Entries</div>
                  <div className="text-sm text-gray-600">
                    {importResults?.entriesImported || 0} imported
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Trophy className="w-6 h-6 text-yellow-600" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Results</div>
                  <div className="text-sm text-gray-600">
                    {importResults?.resultsImported || 0} imported
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Database className="w-6 h-6 text-purple-600" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Memberships</div>
                  <div className="text-sm text-gray-600">
                    {importResults?.membershipsImported || 0} imported
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ImportConfirmation;

